import React from 'react';
import { Col, Row, Form, Select, DatePicker, FormInstance } from 'antd';
import { useQuery } from '@common/hooks';
import { RolesQuery, RoleType } from '@clinician-profile/graphql';
import { RoleLevelsQuery } from 'works/graphql/role-level';
import { ErrorHandler, Gap, InfoTooltip } from '@common/components';
import { RoleSelect } from '@clinician-profile/components';
import { RoleLevelEnum } from '@common/graphql';
import { Moment } from 'moment';
import { RatesLongTermStepFormEmploymentTypeFields } from './_rates-long-term-step-form-employment-type-fields';
import { roleLevelParser } from '@institutions/services';

const contractStartDateTooltipInfo =
  'This can be any date. Note that if you set this to a date in the past, or earlier than any rate cards already in place for this role, level, and unit(s), those rate cards will no longer be in effect.';

type FormValuesType = {
  roleId: string;
  roleLevel: RoleLevelEnum;
  effectiveDate: Moment;
  travelerDayRate: number;
  travelerEveningRate: number;
  travelerNightRate: number;
  travelerVariableRate: number;
  localDayRate: number;
  localEveningRate: number;
  localNightRate: number;
  localVariableRate: number;
  directSourcingDayRate: number;
  directSourcingEveningRate: number;
  directSourcingNightRate: number;
  directSourcingVariableRate: number;
};

export type SubmittedValuesType = {
  role: RoleType;
  roleLevel: RoleLevelEnum;
  effectiveDate: Moment;
  localRate: {
    dayBillingRate: number;
    eveningBillingRate: number;
    nightBillingRate: number;
    variableBillingRate: number;
  };
  directSourcingRate: {
    dayBillingRate: number;
    eveningBillingRate: number;
    nightBillingRate: number;
    variableBillingRate: number;
  };
  travelerRate: {
    dayBillingRate: number;
    eveningBillingRate: number;
    nightBillingRate: number;
    variableBillingRate: number;
  };
};

type RatesLongTermStepFormProps = {
  form: FormInstance<FormValuesType>;
  onFormSubmitted: (values: SubmittedValuesType) => void;
};

export function RatesLongTermStepForm({
  form,
  onFormSubmitted,
}: RatesLongTermStepFormProps) {
  const rolesQuery = useQuery(RolesQuery, {
    fetchPolicy: 'cache-first',
  });
  const roleLevelsQuery = useQuery(RoleLevelsQuery, {
    fetchPolicy: 'cache-first',
  });

  const anyError = rolesQuery.error || roleLevelsQuery.error;

  if (anyError) {
    return (
      <ErrorHandler
        error={rolesQuery.error || roleLevelsQuery.error}
        log="RatesStepsStepTwo"
      />
    );
  }

  const roles = rolesQuery?.data?.roles ?? [];
  const roleLevels = roleLevelsQuery.data?.roleLevels;

  const transformFormSubmit = ({ roleId, ...formValues }: FormValuesType) => {
    const role = roles.find((role) => role.id === roleId);

    if (!role) {
      console.error('Rates Long Term Form: Selected role not found');
      return;
    }

    onFormSubmitted({
      ...formValues,
      roleLevel: formValues.roleLevel,
      effectiveDate: formValues.effectiveDate,
      localRate: {
        dayBillingRate: formValues.localDayRate,
        eveningBillingRate: formValues.localEveningRate,
        nightBillingRate: formValues.localNightRate,
        variableBillingRate: formValues.localVariableRate,
      },
      directSourcingRate: {
        dayBillingRate: formValues.directSourcingDayRate,
        eveningBillingRate: formValues.directSourcingEveningRate,
        nightBillingRate: formValues.directSourcingNightRate,
        variableBillingRate: formValues.directSourcingVariableRate,
      },
      travelerRate: {
        dayBillingRate: formValues.travelerDayRate,
        eveningBillingRate: formValues.travelerEveningRate,
        nightBillingRate: formValues.travelerNightRate,
        variableBillingRate: formValues.travelerVariableRate,
      },
      role,
    });
  };

  return (
    <>
      <div className="rates-long-term-step-form" style={{ maxWidth: '882px' }}>
        <Form layout="vertical" onFinish={transformFormSubmit} form={form}>
          <Row gutter={24}>
            <Col span={14}>
              <RoleSelect
                placeholder="Select role"
                disabled={rolesQuery.loading}
                roles={roles}
                data-testid="role"
                size="middle"
                labelKey="name"
                showLabel
              />
            </Col>
            <Col span={4}>
              <Form.Item
                rules={[{ required: true, message: 'Please select a level' }]}
                name="roleLevel"
                label="Level"
              >
                <Select
                  placeholder="Select level"
                  loading={roleLevelsQuery.loading}
                  disabled={roleLevelsQuery.loading}
                  showSearch
                  options={roleLevels?.map(normalizeRoleLevelOption)}
                  data-testid="level"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: 'Please select a contract start date',
                  },
                ]}
                name="effectiveDate"
                label={
                  <>
                    Contract Start Date
                    <InfoTooltip
                      title={contractStartDateTooltipInfo}
                      leftOffset={8}
                    />
                  </>
                }
              >
                <DatePicker
                  style={{
                    width: '100%',
                  }}
                  format="MM/DD/YYYY"
                  data-testid="effective-date"
                />
              </Form.Item>
            </Col>
          </Row>
          <Gap size="lg">
            <RatesLongTermStepFormEmploymentTypeFields
              type="traveler"
              onBlur={(fieldName) => copyTravelerRates(fieldName, form)}
            />
            <RatesLongTermStepFormEmploymentTypeFields type="local" />
            <RatesLongTermStepFormEmploymentTypeFields type="directSourcing" />
          </Gap>
        </Form>
      </div>
    </>
  );
}

function copyTravelerRates(
  travelerName: string,
  form: FormInstance<FormValuesType>
) {
  const shiftName = travelerName.replace('traveler', '');
  const localName = `local${shiftName}`;
  const directSourcingName = `directSourcing${shiftName}`;

  copyToEmptyField(travelerName, localName, form);
  copyToEmptyField(travelerName, directSourcingName, form);
}

function copyToEmptyField(
  fromName: string,
  toName: string,
  form: FormInstance<FormValuesType>
) {
  if (!form.getFieldValue(toName)) {
    form.setFieldsValue({
      [toName]: form.getFieldValue(fromName),
    });
  }
}

function normalizeRoleLevelOption(roleLevel: RoleLevelEnum) {
  return {
    value: roleLevel,
    label: roleLevelParser(roleLevel),
  };
}
